// src/views/Outbound.jsx
// OUTBOUND DIALER — works the skip-traced contact list (outbound_contacts),
// three leads at a time. Pick the style of lead (year + HECM / lending limit
// / proprietary / FTR / HomeSafe Second), click a number to dial it in
// RingCentral, then log the result on that number:
//   No answer · Left voicemail · Dead line · Wrong number · Picked up and spoke
// Every result is logged on the contact's running log (per card, and the
// Activity log at the bottom). It all stays HERE in outbound_contacts —
// nothing is written onto the lead's file, notes or history (Reece's rule).
// The one exception is the owner tag: whoever logs a call on an unowned lead
// claims it (lead tag "owner:<staff>", never overwriting an existing owner).
// "Picked up and spoke" also opens a notes box; the note saves itself when you
// click away, on "Next 3" and when you leave the screen — no Save click needed.
// When every number on all three cards has a result — or on "Next 3" — the
// next three load.
//
// Who never shows up here at all — not on a card, in follow-ups, in the
// filters or in any count (Reece, 10/1: DNC / litigator rules are for the
// outbound dialer ONLY; "Realistically it just shouldn't show up"):
//   · a lead that is do-not-contact / opted out, or tagged TCPA litigator
//     (fetched from marketing_leads at load, so it never depends on which
//     leads happen to be in memory),
//   · a TCPA litigator contact: its row is flagged, or any of its numbers is
//     a litigator's number (flagged on this row or sitting on ANY litigator's
//     row) — every row of that lead goes with it.
// Only numbers NOT on the Do Not Call list are ever shown.
// A lead that already converted or was worked (a pipeline file, or a status
// past New/Mailed) is kept out of the dialer too — every sibling row of that
// lead goes to "Converted", with an Open file button that opens the EXISTING
// file.
// No license gating: every state's contacts are dialable (BRAND.licensedStates
// is compliance text only). Dead lines are retired everywhere; wrong numbers
// for every row of the same lead. (New Call's phone match still uses every
// number — someone who calls IN is treated like any caller.)

import React, { useState, useEffect, useMemo, useRef } from "react";
import { C } from '../theme';
import { Btn, CallBtn, Hdr, Sel } from '../ui';
import { hecmLimitForYear, FALLOUT_TAGS } from '../constants';
import { fetchLeadById, loadAllRows, loadOutbound, loadOutboundByIds, loadOutboundSince, mergeOutboundRow, prospectFromLead, syncOutboundRow, convertedLoanFor, leadOwner, stampLeadOwner, isoDatePlus } from '../lib';
import { isSupabaseReady } from '../supabase';

const RESULTS=[
  {k:"no_answer",l:"No answer",c:"#7A8AA8"},
  {k:"voicemail",l:"Left voicemail",c:"#CA8A04"},
  {k:"dead_line",l:"Dead line",c:"#C0392B"},
  {k:"wrong_number",l:"Wrong number",c:"#C0392B"},
  {k:"spoke",l:"Picked up and spoke",c:"#0DAA6D"},
];
const RESULT_LABEL=Object.fromEntries(RESULTS.map(r=>[r.k,r.l]));
const BAD=new Set(["dead_line","wrong_number"]);
const RECALL_HOURS=24;      // a contact (or any row of the same lead) attempted within this window waits
const BATCH=3;
const LIST_MAX=300;         // rows drawn at once in the Converted list

// Lead-level flags. A lead never shows up in the dialer when it is
// do-not-contact / opted out or tagged TCPA litigator, and is moved to
// "Converted" once it became a file or was worked past New/Mailed (Responded,
// Consultation, Application, Processing, Closed, Dead, or a Fallout
// disposition).
const OPEN_STATUSES=new Set(["","New","Mailed"]);
const DNC_STATUSES=new Set(["DNC","Opt-Out Requested"]);
const DNC_TAGS=new Set(["opt-out","optout-request"]);
// The tag New Call puts on a lead reached on a litigator's number
// (CallConsole LITIGATOR_TAG) — it keeps that lead out of Outbound.
const LIT_TAG="tcpa-litigator";
const WORKED_TAGS=new Set(Object.values(FALLOUT_TAGS));
// Every flagged lead in one small server query (~570 rows), so the check never
// depends on the in-memory working set.
const LEAD_FLAG_COLS="id,status,tags,do_not_contact,converted_loan_id,state";
const LEAD_FLAG_FILTER=["do_not_contact.is.true","converted_loan_id.not.is.null","status.not.in.(New,Mailed)"]
  .concat(Array.from(DNC_TAGS).concat([LIT_TAG]).concat(Array.from(WORKED_TAGS)).map(function(t){return 'tags.cs.["'+t+'"]';})).join(",");

const STYLES=[
  {k:"all",l:"All"},
  {k:"hecm",l:"HECM refi"},
  {k:"ll",l:"Lending limit"},
  {k:"prop",l:"Proprietary"},
  {k:"ftr",l:"First-time (FTR)"},
  {k:"hs2",l:"HomeSafe Second"},
  {k:"other",l:"Other"},
];
const money=n=>n>0?"$"+Math.round(n/1000).toLocaleString()+"K":"";
const fmtPhone=n=>{const d=String(n||"").replace(/\D/g,"");return d.length===10?"("+d.slice(0,3)+") "+d.slice(3,6)+"-"+d.slice(6):n;};
const dg=n=>String(n||"").replace(/\D/g,"").slice(-10);
const pad2=n=>String(n).padStart(2,"0");
const ymdLocal=d=>d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());
const fmtStamp=d=>d.toLocaleDateString()+" "+d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
const bestFirst=(a,b)=>(b.best?1:0)-(a.best?1:0);

// Mailing dates come as "2026-04-21", "2026-07-18 (est.)", "9/30/2026", and
// looser list formats: "~2026-06-11", "~2026 | 2026-07-10", "2026-04 (Campaign
// 8)", "June 2026", "~May 2026". Month-only dates count as the 1st and are
// flagged approx (shown as "Jun 2026"). Returns {d:"YYYY-MM-DD"|"", approx}.
const MONTH_RE=/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?,?\s+(\d{4})\b/i;
const MONTH_IX={jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
function mailWhen(m){
  const none={d:"",approx:false};
  if(!m)return none;
  if(m.sentAt){
    const t=String(m.sentAt);
    // A timestamp is a moment → the LOCAL calendar day it fell on.
    if(/T\d/.test(t)){const dt=new Date(t);if(!isNaN(dt.getTime()))return{d:ymdLocal(dt),approx:false};}
    const y=/(\d{4})-(\d{2})-(\d{2})/.exec(t);if(y)return{d:y[1]+"-"+y[2]+"-"+y[3],approx:false};
  }
  const s=String(m.dateSent||"").trim();
  let x=/(\d{4})-(\d{2})-(\d{2})/.exec(s);if(x&&+x[2]>=1&&+x[2]<=12)return{d:x[1]+"-"+x[2]+"-"+x[3],approx:false};
  x=/(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);if(x&&+x[1]>=1&&+x[1]<=12)return{d:x[3]+"-"+pad2(x[1])+"-"+pad2(x[2]),approx:false};
  x=/(\d{4})-(\d{2})(?!\d)/.exec(s);if(x&&+x[2]>=1&&+x[2]<=12)return{d:x[1]+"-"+x[2]+"-01",approx:true};
  x=MONTH_RE.exec(s);if(x){const mo=MONTH_IX[x[1].slice(0,3).toLowerCase()];if(mo)return{d:x[2]+"-"+pad2(mo)+"-01",approx:true};}
  return none;
}
// "YYYY-MM-DD" (sortable) or "".
function mailDay(m){return mailWhen(m).d;}
const FU_PICKS=[{d:1,l:"Tomorrow"},{d:3,l:"3 days"},{d:7,l:"1 week"},{d:14,l:"2 weeks"},{d:30,l:"30 days"}];
// The open follow-up on a contact = latest result carrying one, not done.
function openFollowUp(r){const res=r.results||[];for(let i=res.length-1;i>=0;i--){const x=res[i];if(x&&x.followUp)return x.followUpDone?null:{x:x,i:i};}return null;}
const fmtDay=d=>{if(!d)return"";const p=d.split("-");return (+p[1])+"/"+(+p[2])+"/"+p[0];};
const fmtMail=m=>{if(!m||!m.d)return"";if(!m.approx)return fmtDay(m.d);const p=m.d.split("-");return new Date(+p[0],+p[1]-1,1).toLocaleDateString(undefined,{month:"short",year:"numeric"});};

// Which style buckets a contact falls in. "Lending limit" = a HECM whose
// value was over the FHA limit the year it closed, when today's is higher.
function styleBuckets(r,hecmLimit){
  const c=r.product_class;
  if(c==="REFI_HECM"){
    const lim=hecmLimitForYear(r.loan_year,hecmLimit);
    return {hecm:true,ll:!!(lim&&hecmLimit&&lim<hecmLimit&&Number(r.home_value)>lim)};
  }
  return {prop:c==="REFI_PROP",ftr:c==="FTR",hs2:c==="HOMESAFE_SECOND",other:!["REFI_PROP","FTR","HOMESAFE_SECOND"].includes(c)};
}

// Lead → {dnc, lit, worked}. Works on a DB row (do_not_contact) or an app lead
// (doNotContact). dnc / lit = never shown in Outbound; worked = "Converted".
function leadHold(l){
  if(!l)return{dnc:false,lit:false,worked:false};
  const st=String(l.status||"").trim();
  const tags=(l.tags||[]).map(function(t){return String(t||"");});
  const dnc=!!(l.doNotContact||l.do_not_contact)||DNC_STATUSES.has(st)||tags.some(function(t){return DNC_TAGS.has(t);});
  const lit=tags.indexOf(LIT_TAG)>=0;
  const worked=!!l.converted_loan_id||(!OPEN_STATUSES.has(st)&&!DNC_STATUSES.has(st))||tags.some(function(t){return WORKED_TAGS.has(t);});
  return{dnc:dnc,lit:lit,worked:worked};
}

// Rows of the same lead are the same person (44 leads have two rows).
function groupKey(r){return r&&r.lead_id?"L:"+r.lead_id:"R:"+(r&&r.id);}
// A litigator's row: flagged itself, or any number on it is a litigator's
// (flagged on this row, or in `lit` — every number on ANY litigator's row).
function rowIsLitigator(r,lit){
  if(!r)return false;
  if(r.litigator)return true;
  const has=function(n){const d=dg(n);return !!d&&!!lit&&lit.has(d);};
  if((r.phones||[]).some(function(p){return p&&(p.litigator||has(p.n));}))return true;
  if((r.phone_digits||[]).some(has))return true;
  return has(r.best_phone);
}
// A TCPA litigator contact never shows up in Outbound — the whole person (every
// row of the same lead), not just the one number. `ctx` (buildDialCtx) adds the
// cross-row checks; without it only the row itself is read.
function isLitigatorContact(r,ctx){
  if(!r)return false;
  if(ctx&&ctx.litGroups&&ctx.litGroups.has(groupKey(r)))return true;
  return rowIsLitigator(r,ctx?ctx.lit:null);
}
// Cross-row facts the per-row checks need: every number on a litigator's row,
// every number any row flags Do-Not-Call (the same number can be imported
// from several skip-trace files with different flags — one DNC flag anywhere
// keeps it off every card), which people are litigators, dead lines (retired
// for everyone), wrong numbers (retired for every row of that lead), and per
// lead whether anyone spoke to them / when last called.
function buildDialCtx(rows){
  const lit=new Set(),dnc=new Set(),dead=new Map(),wrong=new Map(),groups=new Map();
  const add=function(map,k,id){const a=map.get(k);if(a){if(a.indexOf(id)<0)a.push(id);}else map.set(k,[id]);};
  (rows||[]).forEach(function(r){
    if(!r)return;
    const g=groupKey(r);
    if(r.litigator){
      (r.phones||[]).forEach(function(p){const d=dg(p&&p.n);if(d)lit.add(d);});
      (r.phone_digits||[]).forEach(function(n){const d=dg(n);if(d)lit.add(d);});
      const b=dg(r.best_phone);if(b)lit.add(b);
    }
    (r.phones||[]).forEach(function(p){
      if(!p)return;
      const d=dg(p.n);if(!d)return;
      if(p.litigator)lit.add(d);
      if(p.dnc)dnc.add(d);
    });
    let spoke=r.status==="Spoke";
    (r.results||[]).forEach(function(x){
      if(!x)return;
      const d=dg(x.n);
      if(x.result==="spoke")spoke=true;
      else if(x.result==="dead_line"&&d)add(dead,d,r.id);
      else if(x.result==="wrong_number"&&d)add(wrong,g+"|"+d,r.id);
    });
    const last=r.last_called_at?Date.parse(r.last_called_at)||0:0;
    const o=groups.get(g);
    if(o){o.spoke=o.spoke||spoke;if(last>o.last)o.last=last;o.size++;}else groups.set(g,{spoke:spoke,last:last,size:1});
  });
  // Second pass, now that every litigator number is known: a person with any
  // litigator row is a litigator on every row.
  const litGroups=new Set();
  (rows||[]).forEach(function(r){if(r&&rowIsLitigator(r,lit))litGroups.add(groupKey(r));});
  // numKey changes only when a cross-row number rule changes, so a plain
  // no-answer click doesn't force every row's numbers to be re-checked.
  const ser=function(map){const a=[];map.forEach(function(ids,k){a.push(k+"="+ids.join("+"));});return a.sort().join(",");};
  const numKey=Array.from(lit).sort().join(",")+"|"+Array.from(litGroups).sort().join(",")+"|"+Array.from(dnc).sort().join(",")+"|"+ser(dead)+"|"+ser(wrong);
  return{lit:lit,dnc:dnc,litGroups:litGroups,dead:dead,wrong:wrong,groups:groups,numKey:numKey};
}
// A number made unsafe or retired by ANOTHER row (litigator anywhere,
// Do-Not-Call anywhere, dead line anywhere, wrong number on a sibling row of
// the same lead).
function blockedByOthers(ctx,r,n){
  if(!ctx)return false;
  const d=dg(n);if(!d)return false;
  if(ctx.lit.has(d))return true;
  if(ctx.dnc&&ctx.dnc.has(d))return true;
  const a=ctx.dead.get(d);if(a&&a.some(function(id){return id!==r.id;}))return true;
  const w=ctx.wrong.get(groupKey(r)+"|"+d);if(w&&w.some(function(id){return id!==r.id;}))return true;
  return false;
}

// Numbers allowed in the dialer: not Do-Not-Call, not retired as dead/wrong,
// nothing at all for a litigator contact. Best number first. `ctx`
// (buildDialCtx) adds the cross-row checks; without it only the row is used.
function dialable(r,ctx){
  if(!r||isLitigatorContact(r,ctx))return[];
  const bad=new Set((r.results||[]).filter(function(x){return x&&BAD.has(x.result);}).map(function(x){return x.n;}));
  return (r.phones||[]).filter(function(p){return p&&!p.dnc&&!p.litigator&&!bad.has(p.n)&&!blockedByOthers(ctx,r,p.n);}).sort(bestFirst);
}
// Numbers drawn on a card: as dialable(), except a number this row retired
// AFTER the card went up stays visible (with its result and undo).
function cardNums(r,ctx,since){
  if(!r||isLitigatorContact(r,ctx))return[];
  const before=new Set((r.results||[]).filter(function(x){return x&&BAD.has(x.result)&&!(x.at>=since);}).map(function(x){return x.n;}));
  return (r.phones||[]).filter(function(p){return p&&!p.dnc&&!p.litigator&&!before.has(p.n)&&!blockedByOthers(ctx,r,p.n);}).sort(bestFirst);
}

// Same content apart from the write stamp — a poll that brings back a row
// this tab already has changes nothing.
// (Key order ignored — Postgres jsonb reorders keys.)
function stableJson(v){
  if(v===undefined||v===null)return"null";
  if(typeof v!=="object")return JSON.stringify(v);
  if(Array.isArray(v))return"["+v.map(stableJson).join(",")+"]";
  return"{"+Object.keys(v).filter(function(k){return v[k]!==undefined&&v[k]!==null;}).sort().map(function(k){return JSON.stringify(k)+":"+stableJson(v[k]);}).join(",")+"}";
}
function sameRow(a,b){
  if(a===b)return true;if(!a||!b)return false;
  const x=Object.assign({},a),y=Object.assign({},b);delete x.updated_at;delete y.updated_at;
  try{return stableJson(x)===stableJson(y);}catch(e){return false;}
}
const POLL_MS=30000;
const POLL_OVERLAP_MS=5*60*1000;   // updated_at is stamped by each user's clock — re-read a few minutes back

function Outbound({mktgLeads,leadsReady,adoptLeads,onStartCall,hecmLimit,staff,loans,setSel,setNav,setMktgLeads,registerFlush}){
  const staffKey=staff||"reece";
  const [rows,setRows]=useState(null);
  const [missing,setMissing]=useState(false);
  const [err,setErr]=useState("");
  const [flagRows,setFlagRows]=useState(null);    // flagged marketing_leads (DNC / litigator / converted / worked)
  const [flagErr,setFlagErr]=useState("");
  const [f,setF]=useState({style:"all",year:"",state:""});
  const [batch,setBatch]=useState([]);           // contact ids on screen
  const [shownAt,setShownAt]=useState({});       // id → ISO when that card went up (results before it don't count)
  const [skipped,setSkipped]=useState([]);       // passed over this session → back of the line
  const [noteFor,setNoteFor]=useState({});       // id → note draft (open after "spoke")
  const [doneNote,setDoneNote]=useState({});     // id → true once the spoke note is closed
  const [logOpen,setLogOpen]=useState({});       // id → running log expanded
  const [fuFor,setFuFor]=useState({});           // id → {on, date} follow-up toggle on the spoke note
  const [tab,setTab]=useState("dialer");         // dialer | followups | converted
  const sessionStart=useRef(new Date().toISOString());
  const rowsRef=useRef(null);                    // newest rows, updated synchronously by patch()
  const noteLine=useRef({});                     // id → {at,text,line,stamp} the note already written for the open spoke
  const stamped=useRef(new Set());               // lead ids already owner-stamped this session
  const cur=useRef({});                          // latest note state, for blur / unmount flushes
  const pinned=useRef(null);                     // id put up by "Call now" — background refills leave it alone
  // ── Shared with the other staff member ──
  // syncedRef: id → the last copy of that row this tab read from or wrote to
  // the server (the BASE of every three-way merge). syncQ: id → the sync in
  // flight for that row (one at a time per row; edits made meanwhile ride in
  // the next pass). pollMark: newest updated_at seen, for the 30s refresh.
  const syncedRef=useRef(new Map());
  const syncQ=useRef(new Map());
  const pollMark=useRef("");
  const ctxRef=useRef(null);
  const [swapNote,setSwapNote]=useState("");
  const live=useRef(true);
  useEffect(function(){live.current=true;return function(){live.current=false;};},[]);
  cur.current={noteFor:noteFor,fuFor:fuFor,doneNote:doneNote,shownAt:shownAt};

  useEffect(function(){
    let live=true;
    const startedAt=Date.now();
    Promise.resolve().then(loadOutbound).then(function(r){
      if(!live)return;
      if(r.missing){setMissing(true);rowsRef.current=[];setRows([]);return;}
      if(r.error){setErr("Couldn't load the outbound list ("+(r.error.message||"unknown")+").");rowsRef.current=[];setRows([]);return;}
      rowsRef.current=r.data||[];
      syncedRef.current=new Map(rowsRef.current.map(function(x){return [x.id,x];}));
      pollMark.current=new Date(startedAt-POLL_OVERLAP_MS).toISOString();
      setRows(rowsRef.current);
    }).catch(function(e){if(!live)return;setErr("Couldn't load the outbound list ("+((e&&e.message)||"database not connected")+").");rowsRef.current=[];setRows([]);});
    if(!isSupabaseReady())setFlagRows([]);// local mode: every lead is already in memory
    else Promise.resolve().then(function(){return loadAllRows("marketing_leads",LEAD_FLAG_COLS,null,LEAD_FLAG_FILTER);}).then(function(r){
      if(!live)return;
      if(r.error){setFlagErr(r.error.message||"unknown");return;}
      setFlagRows(r.data||[]);
    }).catch(function(e){if(live)setFlagErr((e&&e.message)||"unknown");});
    return function(){live=false;};
  },[]);

  const all=useMemo(function(){return rows||[];},[rows]);
  // Opt-out / litigator flags must be known before anyone is offered. The
  // server query is the source; if it failed, the login working set (which
  // holds every DNC / worked lead) stands in once it has loaded.
  const flagsOk=flagRows!==null||(!!flagErr&&!!leadsReady);

  // Most recent mailing per lead. Every mailed lead is in memory (the login
  // working set), so no extra download; never-mailed → no entry.
  // Cached per lead object, so an edit to one lead doesn't re-parse 38K.
  const mailCache=useRef(new WeakMap());
  const lastMail=useMemo(function(){
    const m=new Map();
    const today=isoDatePlus(0); // LOCAL day; future-dated (scheduled) drops don't count yet
    (mktgLeads||[]).forEach(function(l){
      if(!l)return;
      const hit=mailCache.current.get(l);
      let best=hit&&hit.today===today?hit.best:undefined;
      if(best===undefined){
        best=null;
        (l.mailings||[]).forEach(function(x){const w=mailWhen(x);if(w.d&&w.d<=today&&(!best||w.d>best.d||(w.d===best.d&&best.approx&&!w.approx)))best={d:w.d,approx:w.approx,campaign:x.campaign||x.batch||""};});
        mailCache.current.set(l,{today:today,best:best});
      }
      if(best)m.set(l.id,best);
    });
    return m;
  },[mktgLeads]);
  const mailOf=function(r){return r.lead_id?lastMail.get(r.lead_id)||null:null;};
  const styleOf=useMemo(function(){
    return function(r){return styleBuckets(r,hecmLimit);};
  },[hecmLimit]);

  // Lead id → {dnc, lit, worked, status, convId, state, loan}. Union of the
  // server flags and the in-memory leads (this session's edits), plus any file
  // whose sourceLeadId points at the lead. Only flagged leads get an entry.
  // Kept as the SAME Map while its content is unchanged (an owner stamp or a
  // poll that changes nothing here), so the per-row cache below survives.
  const holdCache=useRef(new WeakMap());
  const leadInfoRef=useRef({sig:null,m:null});
  const leadInfo=useMemo(function(){
    const m=new Map();
    const put=function(l,mem){
      if(!l||!l.id)return;
      let h=holdCache.current.get(l);if(!h){h=leadHold(l);holdCache.current.set(l,h);}
      if(!h.dnc&&!h.lit&&!h.worked)return;
      const o=m.get(l.id)||{id:l.id,dnc:false,lit:false,worked:false,status:"",convId:"",state:"",loan:null};
      o.dnc=o.dnc||h.dnc;o.lit=o.lit||h.lit;o.worked=o.worked||h.worked;
      if(mem||!o.status)o.status=l.status||o.status;
      o.convId=l.converted_loan_id||o.convId;o.state=o.state||l.state||"";
      m.set(l.id,o);
    };
    (flagRows||[]).forEach(function(l){put(l,false);});
    (mktgLeads||[]).forEach(function(l){put(l,true);});
    const ls=loans||[];
    ls.forEach(function(l){if(l&&l.sourceLeadId&&!m.has(l.sourceLeadId))m.set(l.sourceLeadId,{id:l.sourceLeadId,dnc:false,lit:false,worked:true,status:"",convId:"",state:"",loan:null});});
    m.forEach(function(o){if(o.worked)o.loan=convertedLoanFor({id:o.id,converted_loan_id:o.convId},ls);});
    let sig="";m.forEach(function(o){sig+=o.id+(o.dnc?"!d":"")+(o.lit?"!l":"")+(o.worked?"!w":"")+"|"+o.status+"|"+o.convId+"|"+o.state+"|"+(o.loan?o.loan.id+":"+(o.loan.stage||""):"")+";";});
    if(leadInfoRef.current.sig===sig)return leadInfoRef.current.m;
    leadInfoRef.current={sig:sig,m:m};
    return m;
  },[flagRows,mktgLeads,loans]);

  const ctx=useMemo(function(){return buildDialCtx(all);},[all]);
  ctxRef.current=ctx;
  // Per contact (h): whether it never shows up (hide: opted out / litigator),
  // whether it is held in "Converted", how many numbers it has left, its lead
  // group and style buckets. Recomputed only for rows that changed (a result
  // click touches one row) unless the lead flags, the HECM limit or a
  // cross-row number rule changed — never per keystroke.
  const infoCache=useRef({key:null,leadInfo:null,rows:new Map(),map:new Map()});
  const infoAll=useMemo(function(){
    const c=infoCache.current;
    const key=ctx.numKey+"|"+hecmLimit;
    const reuse=c.key===key&&c.leadInfo===leadInfo;
    const m=new Map(),seen=new Map(),list=[];
    all.forEach(function(r){
      let h=reuse&&c.rows.get(r.id)===r?c.map.get(r.id):null;
      if(!h){
        const li=r.lead_id?leadInfo.get(r.lead_id)||null:null;
        const st=String(r.state||(li&&li.state)||"").trim().toUpperCase();
        h={r:r,g:groupKey(r),sb:styleOf(r),st:st,conv:li&&li.worked?li:null};
        h.hide=!!(li&&(li.dnc||li.lit))||isLitigatorContact(r,ctx);
        h.held=h.hide||!!h.conv;
        h.n=h.held?0:dialable(r,ctx).length;
      }
      m.set(r.id,h);seen.set(r.id,r);list.push(h);
    });
    infoCache.current={key:key,leadInfo:leadInfo,rows:seen,map:m};
    return{m:m,list:list};
  },[all,ctx,leadInfo,styleOf]);// eslint-disable-line react-hooks/exhaustive-deps
  const info=infoAll.m;
  // Ready for a cold call: not held, numbers left, nobody on this lead has
  // been reached yet.
  const elig=useMemo(function(){
    if(!flagsOk)return[];
    return infoAll.list.filter(function(h){
      if(h.held||!h.n||h.r.status==="Spoke")return false;
      const g=ctx.groups.get(h.g);
      return !(g&&g.spoke);
    });
  },[infoAll,ctx,flagsOk]);

  // Everyone dialable in this style/state (year handled after, so the year
  // menu can show counts). Recently attempted contacts — or any row of the
  // same lead — wait RECALL_HOURS.
  const pool=useMemo(function(){
    const cutoff=Date.now()-RECALL_HOURS*3600e3;
    return elig.filter(function(h){
      if(f.state&&h.st!==f.state)return false;
      if(f.style!=="all"&&!h.sb[f.style])return false;
      if(batch.indexOf(h.r.id)<0){const g=ctx.groups.get(h.g);if(g&&g.last>cutoff)return false;}
      return true;
    });
  },[elig,ctx,f.style,f.state,batch]);
  const years=useMemo(function(){
    const c={};pool.forEach(function(h){const y=h.r.loan_year;if(y)c[y]=(c[y]||0)+1;});
    return Object.keys(c).map(Number).sort(function(a,b){return b-a;}).map(function(y){return{value:String(y),label:y+" ("+c[y].toLocaleString()+")"};});
  },[pool]);
  const queue=useMemo(function(){
    const skip=new Set(skipped);
    // Sort keys computed once per row (not per comparison):
    // skipped last · test contacts (id TEST-…) first so a test call is easy to
    // find · most recently mailed first, never-mailed last · least recently called.
    const keyed=pool.filter(function(h){return !f.year||String(h.r.loan_year)===f.year;}).map(function(h){
      const r=h.r;
      return{r:r,g:h.g,s:skip.has(r.id)?1:0,t:String(r.id).indexOf("TEST-")===0?0:1,m:(mailOf(r)||{}).d||"",c:r.last_called_at?Date.parse(r.last_called_at)||0:0};
    });
    keyed.sort(function(a,b){return a.s-b.s||a.t-b.t||(a.m!==b.m?(a.m<b.m?1:-1):0)||a.c-b.c;});
    // One row per person: a lead's second contact row waits its turn.
    const seen=new Set();
    return keyed.filter(function(k){if(seen.has(k.g))return false;seen.add(k.g);return true;}).map(function(k){return k.r;});
  },[pool,f.year,skipped,lastMail]);// eslint-disable-line react-hooks/exhaustive-deps
  // Every state on the list — no license gating (opted-out / litigator
  // contacts don't count toward the menu either).
  const states=useMemo(function(){
    const s=new Set();infoAll.list.forEach(function(h){if(h.st&&!h.hide)s.add(h.st);});
    return Array.from(s).sort();
  },[infoAll]);
  // Chip counts, one pass (people, not rows).
  const styleCounts=useMemo(function(){
    const c={all:0,hecm:0,ll:0,prop:0,ftr:0,hs2:0,other:0};
    const seen=new Set();
    elig.forEach(function(h){
      if((f.state&&h.st!==f.state)||seen.has(h.g))return;seen.add(h.g);
      c.all++;Object.keys(h.sb).forEach(function(k){if(h.sb[k]&&c[k]!=null)c[k]++;});
    });
    return c;
  },[elig,f.state]);

  // Held-out list: converted / worked (by lead). Opted-out and litigator
  // contacts are not listed or counted anywhere.
  const held=useMemo(function(){
    const conv=new Map();
    infoAll.list.forEach(function(h){
      if(h.hide||!h.conv)return;
      const r=h.r,g=h.g;
      const o=conv.get(g)||{key:g,li:h.conv,h:h,rows:[]};o.rows.push(r);conv.set(g,o);
    });
    const byName=function(a,b){return String(a.rows[0].name||"").localeCompare(String(b.rows[0].name||""));};
    const convL=Array.from(conv.values()).sort(function(a,b){return (a.li.loan?0:1)-(b.li.loan?0:1)||byName(a,b);});
    return{conv:convL};
  },[infoAll]);

  const sinceOf=function(id){const s=shownAt[id];return s&&s>sessionStart.current?s:sessionStart.current;};
  const sinceResults=function(r){const s=sinceOf(r.id);return (r.results||[]).filter(function(x){return x&&x.at>=s;});};

  // ── Keeping up with the other staff member ──
  // Take server copies of rows this tab has no unsaved edit on (a row with a
  // sync queued or an edit not yet synced merges the server copy itself).
  const adoptServerRows=function(srvRows){
    if(!srvRows||!srvRows.length||!rowsRef.current)return 0;
    const take=new Map();
    srvRows.forEach(function(s){
      if(!s||!s.id||syncQ.current.has(s.id))return;
      const base=syncedRef.current.get(s.id);
      const mine=latestRow(s.id);
      if(mine&&base!==mine)return;              // edited here, not synced yet
      if(mine&&sameRow(mine,s))return;
      take.set(s.id,s);
    });
    if(!take.size)return 0;
    const have=new Set();
    const next=rowsRef.current.map(function(r){have.add(r.id);const t=take.get(r.id);return t||r;});
    take.forEach(function(s,id){if(!have.has(id))next.push(s);});
    take.forEach(function(s,id){syncedRef.current.set(id,s);});
    rowsRef.current=next;setRows(next);
    return take.size;
  };
  const markSeen=function(list){
    let mt=Date.parse(pollMark.current)||0;
    (list||[]).forEach(function(s){const t=s&&s.updated_at?Date.parse(s.updated_at)||0:0;if(t>mt){mt=t;pollMark.current=String(s.updated_at);}});
  };
  // Every 30s, and when the tab comes back into focus: rows anyone changed.
  useEffect(function(){
    if(rows===null||missing||!isSupabaseReady())return;
    let live=true;let lastRun=0;
    const tick=function(){
      lastRun=Date.now();
      const since=pollMark.current?new Date((Date.parse(pollMark.current)||Date.now())-POLL_OVERLAP_MS).toISOString():new Date(Date.now()-POLL_OVERLAP_MS).toISOString();
      Promise.resolve(loadOutboundSince(since)).then(function(r){if(!live||!r||r.error)return;adoptServerRows(r.data);markSeen(r.data);}).catch(function(){});
    };
    const onVis=function(){if(!document.hidden&&Date.now()-lastRun>5000)tick();};
    const t=setInterval(tick,POLL_MS);
    document.addEventListener("visibilitychange",onVis);window.addEventListener("focus",onVis);
    return function(){live=false;clearInterval(t);document.removeEventListener("visibilitychange",onVis);window.removeEventListener("focus",onVis);};
  },[rows===null,missing]);// eslint-disable-line react-hooks/exhaustive-deps

  // Put the next three on screen (excluding the ones just worked). The picks
  // (and a few spares) are re-read from the server first-hand: if the other
  // staff member called one in the meantime, the swap effect below replaces it.
  const fill=function(excl){
    const ex=new Set(excl||[]);
    const cands=queue.filter(function(r){return !ex.has(r.id);});
    const ids=cands.slice(0,BATCH).map(function(r){return r.id;});
    const now=new Date().toISOString();
    pinned.current=null;
    setBatch(ids);
    setShownAt(function(s){const o=Object.assign({},s);ids.forEach(function(id){o[id]=now;});return o;});
    if(isSupabaseReady()&&ids.length){
      const check=cands.slice(0,BATCH+6).map(function(r){return r.id;});
      Promise.resolve(loadOutboundByIds(check)).then(function(r){if(r&&!r.error)adoptServerRows(r.data);}).catch(function(){});
    }
  };
  const ready=rows!==null&&flagsOk;
  useEffect(function(){if(ready)fill([]);},[ready,f.style,f.year,f.state]);// eslint-disable-line react-hooks/exhaustive-deps

  const byId=useMemo(function(){return new Map(all.map(function(r){return [r.id,r];}));},[all]);
  // A contact that turned opted-out / litigator while on screen leaves the
  // cards — unless it was already worked this session (its result or note
  // stays visible until Next 3). The effect below swaps it for the next one.
  const workedHere=function(r){return sinceResults(r).length>0||Object.prototype.hasOwnProperty.call(noteFor,r.id);};
  const cards=batch.map(function(id){return byId.get(id);}).filter(function(r){
    if(!r)return false;
    const h=info.get(r.id);
    return !(h&&h.hide)||workedHere(r);
  });
  // Mail history (or the lead flags) landed after the first three were picked:
  // re-pick them, as long as nothing has been logged on them yet.
  const hasMail=lastMail.size>0;
  useEffect(function(){
    if(!ready||!leadsReady||pinned.current)return;
    const touched=cards.some(function(r){return sinceResults(r).length>0;})||Object.keys(noteFor).length>0;
    if(!touched)fill([]);
  },[leadsReady,hasMail]);// eslint-disable-line react-hooks/exhaustive-deps

  // A card the other staff member is working (called within the recall window
  // since it went up here, and nothing logged on it by this session) is
  // swapped for the next contact — two people never dial the same senior.
  // A card that turned opted-out / litigator (nothing logged on it here) is
  // swapped out quietly.
  useEffect(function(){
    if(!batch.length||!ready)return;
    const cutoff=Date.now()-RECALL_HOURS*3600e3;
    const stale=new Set(),gone=new Set();
    batch.forEach(function(id){
      const r=byId.get(id);if(!r)return;
      const since=sinceOf(id);
      const res=r.results||[];
      const mine=res.some(function(x){return x&&x.at>=since&&x.by===staffKey;})||noteFor[id]!==undefined;
      if(mine)return;
      const h=info.get(id);
      if(h&&h.hide&&!workedHere(r)){gone.add(id);return;}
      if(pinned.current===id)return;
      const last=r.last_called_at?Date.parse(r.last_called_at)||0:0;
      if(last>cutoff)stale.add(id);
    });
    if(!stale.size&&!gone.size)return;
    if(gone.has(pinned.current))pinned.current=null;
    const out=function(id){return stale.has(id)||gone.has(id);};
    const keep=batch.filter(function(id){return !out(id);});
    const add=queue.filter(function(r){return keep.indexOf(r.id)<0&&!out(r.id);}).slice(0,BATCH-keep.length).map(function(r){return r.id;});
    const now=new Date().toISOString();
    setBatch(keep.concat(add));
    setShownAt(function(s){const o=Object.assign({},s);add.forEach(function(id){o[id]=now;});return o;});
    if(!stale.size)return;
    const names=Array.from(stale).map(function(id){const r=byId.get(id);return (r&&r.name)||id;});
    setSwapNote(names.join(", ")+(names.length===1?" was":" were")+" just called by someone else — swapped for the next contact.");
  },[all,batch,ready,infoAll]);// eslint-disable-line react-hooks/exhaustive-deps

  const latestRow=function(id){return (rowsRef.current||[]).find(function(r){return r.id===id;})||null;};
  // status / last result follow the MERGED results (both users' dials).
  const finalizeRow=function(m){
    const res=m.results||[];
    const remaining=dialable(m,ctxRef.current);
    m.status=res.some(function(x){return x&&x.result==="spoke";})?"Spoke":res.length?(remaining.length?"Attempted":"Bad numbers"):"New";
    return m;
  };
  const failSave=function(e){setErr("A result didn't save ("+((e&&e.message)||"unknown")+") — check your connection.");};
  // Push this row to the server (three-way merged with whatever the other
  // user wrote). One sync per row at a time; an edit made while one is in
  // flight runs another pass when it lands.
  const retries=useRef(new Map());
  const syncRow=function(id){
    const q=syncQ.current.get(id);
    if(q){q.again=true;return q.p;}
    const job={again:false,p:null};
    syncQ.current.set(id,job);
    job.p=(async function(){
      await null;
      try{
        do{
          job.again=false;
          const local=latestRow(id);if(!local)break;
          const base=syncedRef.current.get(id)||null;
          const res=await syncOutboundRow(id,local,base,finalizeRow);
          if(!res||res.error){
            failSave(res&&res.error);
            // A blip: try again shortly (a few times), so the edit isn't left
            // only in this tab.
            const n=(retries.current.get(id)||0)+1;retries.current.set(id,n);
            if(n<=3)setTimeout(function(){if(live.current)syncRow(id);},8000*n);
            break;
          }
          if(retries.current.has(id)){retries.current.delete(id);setErr(function(e){return /didn't save/.test(e||"")?"":e;});}
          syncedRef.current.set(id,res.data);
          // Take the merged row (the other user's dials included). If it was
          // edited again meanwhile, those newer edits are re-applied on top of
          // the merged row and the next pass writes them.
          const latest=latestRow(id);
          let shown=res.data;
          if(latest&&latest!==local){
            shown=finalizeRow(Object.assign({},latest,mergeOutboundRow(latest,local,res.data)));
            job.again=true;
          }
          const next=(rowsRef.current||[]).map(function(r){return r.id===id?shown:r;});
          rowsRef.current=next;setRows(next);
        }while(job.again);
      }catch(e){failSave(e);}
      finally{syncQ.current.delete(id);}
    })();
    return job.p;
  };
  const patch=function(id,p){
    const next=(rowsRef.current||[]).map(function(r){return r.id===id?Object.assign({},r,p):r;});
    rowsRef.current=next;setRows(next);
    if(!isSupabaseReady())return Promise.resolve();
    return syncRow(id);
  };
  // Everything still being written (idle sign-out / logout wait on this).
  const pendingSyncs=function(){return Promise.all(Array.from(syncQ.current.values()).map(function(j){return j.p;}));};

  // Whoever logs a call on an unowned lead claims it (a tracking tag; an
  // existing owner is never overwritten). Once per lead per session.
  const stampOwner=function(leadId){
    if(!leadId||!setMktgLeads||stamped.current.has(leadId))return;
    stamped.current.add(leadId);
    const apply=function(){setMktgLeads(function(prev){return (prev||[]).map(function(l){return l&&l.id===leadId?stampLeadOwner(l,staffKey):l;});});};
    const have=(mktgLeads||[]).find(function(m){return m&&m.id===leadId;});
    if(have){if(!leadOwner(have))apply();return;}
    if(!adoptLeads)return;
    Promise.resolve(fetchLeadById(leadId)).then(function(lead){if(!lead||leadOwner(lead))return;adoptLeads([lead]);apply();}).catch(function(){stamped.current.delete(leadId);});
  };

  const record=function(r0,n,result){
    const r=latestRow(r0.id)||r0;
    const at=new Date().toISOString();
    const entry={n:n,result:result,at:at,by:staffKey};
    let results=(r.results||[]).concat([entry]);
    const remaining=dialable(Object.assign({},r,{results:results}),ctx);
    // Reaching them closes an open follow-up. A dead/wrong number only does
    // when it was their last number — otherwise you still owe them the call.
    const closesFu=result==="spoke"||(BAD.has(result)&&!remaining.length);
    if(closesFu)results=results.map(function(x){return x!==entry&&x&&x.followUp&&!x.followUpDone?Object.assign({},x,{followUpDone:true,followUpDoneAt:at}):x;});
    const spokeEver=results.some(function(x){return x&&x.result==="spoke";});
    const status=spokeEver?"Spoke":remaining.length?"Attempted":"Bad numbers";
    patch(r.id,{results:results,last_result:result,status:status,call_count:(r.call_count||0)+1,last_called_at:at});
    if(result==="spoke")setNoteFor(function(x){return Object.assign({},x,{[r.id]:x[r.id]||""});});
    stampOwner(r.lead_id);
  };
  const undo=function(r0,n){
    const r=latestRow(r0.id)||r0;
    const since=sinceOf(r.id);
    const res=(r.results||[]).slice();let removed=null;
    for(let i=res.length-1;i>=0;i--){if(res[i]&&res[i].n===n&&res[i].at>=since){removed=res.splice(i,1)[0];break;}}
    if(!removed)return;
    // Re-open any follow-up that this result closed.
    const back=res.map(function(x){if(!(x&&x.followUpDone&&x.followUpDoneAt===removed.at))return x;const o=Object.assign({},x);delete o.followUpDone;delete o.followUpDoneAt;return o;});
    const remaining=dialable(Object.assign({},r,{results:back}),ctx);
    const lastAt=back.reduce(function(m,x){return x&&x.at&&x.at>m?x.at:m;},"");
    const status=back.some(function(x){return x&&x.result==="spoke";})?"Spoke":back.length?(remaining.length?"Attempted":"Bad numbers"):"New";
    patch(r.id,{results:back,last_result:back.length?back[back.length-1].result:"",status:status,call_count:Math.max(0,(r.call_count||1)-1),last_called_at:lastAt||null});
    if(removed.result==="spoke"&&!back.some(function(x){return x&&x.result==="spoke"&&x.at>=since;})){
      setDoneNote(function(x){const o=Object.assign({},x);delete o[r.id];return o;});
      setNoteFor(function(x){const o=Object.assign({},x);delete o[r.id];return o;});
      setFuFor(function(x){const o=Object.assign({},x);delete o[r.id];return o;});
      delete noteLine.current[r.id];
    }
  };

  // Write the spoke note (and/or follow-up) onto the spoke result and the
  // contact's notes — ONE entry per spoke: saving again edits that entry in
  // place, it never adds a second line. Reads only refs, so blur / Next 3 /
  // unmount can all call it safely.
  const commitNote=function(id,text,fu){
    const r=latestRow(id);if(!r)return;
    const sh=(cur.current.shownAt||{})[id];
    const since=sh&&sh>sessionStart.current?sh:sessionStart.current;
    const spoke=(r.results||[]).filter(function(x){return x&&x.result==="spoke"&&x.at>=since;}).slice(-1)[0];
    if(!spoke)return;
    const t=String(text||"").trim();
    const prev=noteLine.current[id]&&noteLine.current[id].at===spoke.at?noteLine.current[id]:null;
    const add={};
    if(t&&(!prev||prev.text!==t))add.note=t;
    if(fu&&spoke.followUp!==fu)add.followUp=fu;
    if(!Object.keys(add).length)return;
    const p={results:(r.results||[]).map(function(x){return x===spoke?Object.assign({},x,add):x;})};
    if(add.note){
      const stamp=prev?prev.stamp:fmtStamp(new Date());
      const line=stamp+" — spoke ("+fmtPhone(spoke.n)+"): "+t;
      let notes=r.notes||"";const i=prev?notes.lastIndexOf(prev.line):-1;
      notes=i>=0?notes.slice(0,i)+line+notes.slice(i+prev.line.length):(notes?notes+"\n":"")+line;
      p.notes=notes;
      noteLine.current[id]={at:spoke.at,text:t,line:line,stamp:stamp};
    }
    patch(id,p);
  };
  const closeNotes=function(ids){
    if(!ids.length)return;
    setFuFor(function(x){const o=Object.assign({},x);ids.forEach(function(id){delete o[id];});return o;});
    setNoteFor(function(x){const o=Object.assign({},x);ids.forEach(function(id){delete o[id];});return o;});
    setDoneNote(function(x){const o=Object.assign({},x);ids.forEach(function(id){o[id]=true;});return o;});
    // noteLine is kept (keyed by the spoke's time): an unmount flush that
    // still sees the old draft then finds it already written — no 2nd line.
  };
  // Save every open spoke note (text + follow-up) — for Next 3, Call now and
  // leaving the screen. ids null = all.
  const flushDrafts=function(ids,close){
    const s=cur.current;const done=[];
    Object.keys(s.noteFor||{}).forEach(function(id){
      if(ids&&ids.indexOf(id)<0)return;
      if((s.doneNote||{})[id])return;
      const fo=(s.fuFor||{})[id];
      commitNote(id,s.noteFor[id],fo&&fo.on&&fo.date?fo.date:"");
      done.push(id);
    });
    if(close)closeNotes(done);
  };
  useEffect(function(){return function(){flushDrafts(null,false);};},[]);// eslint-disable-line react-hooks/exhaustive-deps
  // Logout / the 30-minute idle sign-out run this BEFORE the session ends: the
  // open spoke notes are written and every result still being saved finishes
  // while this tab can still write.
  const flushRef=useRef(null);
  flushRef.current=function(){flushDrafts(null,false);return pendingSyncs();};
  useEffect(function(){
    if(!registerFlush)return;
    registerFlush("outbound",function(){return flushRef.current?flushRef.current():null;});
    return function(){registerFlush("outbound",null);};
  },[registerFlush]);

  const saveNote=function(r,skip){
    const fo=fuFor[r.id];const fu=fo&&fo.on&&fo.date?fo.date:"";
    const text=String(noteFor[r.id]||"").trim();
    if(!skip&&!text&&!fu)return;
    commitNote(r.id,skip?"":text,fu);
    closeNotes([r.id]);
  };
  const openLead=async function(r){
    if(!r.lead_id||!onStartCall)return;
    let lead=(mktgLeads||[]).find(function(m){return m.id===r.lead_id;});
    if(!lead){lead=await fetchLeadById(r.lead_id);if(lead&&adoptLeads)adoptLeads([lead]);}
    if(lead)onStartCall({prospect:prospectFromLead(lead)});
    else setErr("Couldn't find lead "+r.lead_id+".");
  };
  // Opens the EXISTING pipeline file — never mints a new one.
  const canOpenFile=!!(setSel&&setNav);
  const openFile=function(loan){
    if(!loan||!canOpenFile)return;
    const fresh=(loans||[]).find(function(l){return l&&l.id===loan.id;})||loan;
    flushDrafts(null,true);setSel(fresh);setNav("loanDetail");
  };

  const isResolved=function(r){
    const s=sinceResults(r);
    if(s.some(function(x){return x.result==="spoke";}))return !!doneNote[r.id];
    const h=info.get(r.id);if(h&&h.held)return true;
    const nums=cardNums(r,ctx,sinceOf(r.id)).map(function(p){return p.n;});
    return nums.every(function(n){return s.some(function(x){return x.n===n;});});
  };
  const next=function(){
    const unresolved=cards.filter(function(r){return !isResolved(r);}).map(function(r){return r.id;});
    flushDrafts(batch,true);
    setSkipped(function(s){return s.concat(unresolved.filter(function(id){return s.indexOf(id)<0;}));});
    fill(batch);
  };
  // All three done → bring up the next three automatically.
  const allDone=cards.length>0&&cards.every(isResolved);
  useEffect(function(){
    if(!allDone)return;
    const t=setTimeout(function(){fill(batch);},900);
    return function(){clearTimeout(t);};
  },[allDone]);// eslint-disable-line react-hooks/exhaustive-deps

  // Follow-ups: only contacts that may still be called. Converted ones are
  // counted (work them from the file); opted-out / litigator contacts never
  // show up, not even in the count.
  const fuData=useMemo(function(){
    const list=[];let hidden=0;
    all.forEach(function(r){const o=openFollowUp(r);if(!o)return;const h=info.get(r.id);if(h&&h.hide)return;if(h&&h.held){hidden++;return;}list.push({r:r,x:o.x,i:o.i});});
    list.sort(function(a,b){return a.x.followUp<b.x.followUp?-1:a.x.followUp>b.x.followUp?1:0;});
    return{list:list,hidden:hidden};
  },[all,info]);
  const stats=useMemo(function(){
    const since=function(from){
      const o={dials:0,people:new Set(),spoke:0,voicemail:0,no_answer:0,bad:0,fu:0};
      all.forEach(function(r){(r.results||[]).forEach(function(x){
        if(!x||!x.at||x.at<from)return;
        o.dials++;o.people.add(r.id);
        if(x.result==="spoke")o.spoke++;else if(x.result==="voicemail")o.voicemail++;else if(x.result==="no_answer")o.no_answer++;else if(BAD.has(x.result))o.bad++;
        if(x.followUp)o.fu++;
      });});
      return o;
    };
    const d0=new Date();d0.setHours(0,0,0,0);
    return [{l:"This session",o:since(sessionStart.current)},{l:"Today",o:since(d0.toISOString())},{l:"All time",o:since("")}];
  },[all]);
  // Activity log: every dial, minus contacts that have since opted out or
  // turned out to be litigators (they never show up here).
  const acts=useMemo(function(){
    const a=[];
    all.forEach(function(r){const h=info.get(r.id);if(h&&h.hide)return;(r.results||[]).forEach(function(x){if(x)a.push({r:r,x:x});});});
    a.sort(function(p,q){return q.x.at<p.x.at?-1:q.x.at>p.x.at?1:0;});
    return a;
  },[all,info]);

  if(rows===null)return(<div><Hdr t="Outbound" s="Dial three at a time"/><div style={{color:C.textDim,fontSize:13}}>Loading…</div></div>);
  if(missing)return(<div><Hdr t="Outbound" s="Dial three at a time"/>
    <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:20,fontSize:13,color:C.textMuted,lineHeight:1.6}}>
      The outbound list isn't set up yet. Run <strong style={{color:C.text}}>supabase/2026-09-30-outbound-contacts.sql</strong> in the Supabase SQL Editor once, then the contacts can be loaded.
    </div></div>);

  const chip=function(on){return{padding:"8px 14px",borderRadius:20,cursor:"pointer",fontSize:12.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?C.accent:C.border),background:on?C.accent:C.card,color:on?"#fff":C.textMuted};};
  const tag=function(col,bg){return{fontSize:10.5,fontWeight:800,color:col,background:bg||C.surfaceAlt,border:"1px solid "+col+"44",borderRadius:6,padding:"2px 7px"};};
  const badges=function(r){
    const s=styleOf(r);
    return [s.ll?"Lending limit":null,s.hecm?"HECM":s.prop?"Proprietary":s.ftr?"FTR":s.hs2?"HomeSafe 2nd":null,
      r.loan_year?"Closed "+r.loan_year:null,money(Number(r.home_value))?"Value "+money(Number(r.home_value)):null,
      money(Number(r.loan_balance))?(/^REFI_/.test(r.product_class||"")?"Lien ":"Bal ")+money(Number(r.loan_balance)):null,r.lender||null].filter(Boolean);
  };
  const holdTags=function(h){
    if(!h)return null;
    return(<>
      {h.conv&&!h.hide&&<span style={tag(C.green)}>{h.conv.loan?"File "+h.conv.loan.id+(h.conv.loan.stage?" · "+h.conv.loan.stage:""):h.conv.convId?"File "+h.conv.convId:"Lead: "+(h.conv.status||"worked")}</span>}
      {h.hide&&<span style={tag(C.textMuted)}>Off the dialer</span>}
    </>);
  };
  const addr=function(r){return [r.address,r.city,r.state].filter(Boolean).join(", ");};
  const listCard={background:C.card,border:"1px solid "+C.border,borderRadius:10,padding:"12px 16px"};
  const empty={padding:"26px 20px",textAlign:"center",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13};
  const fus=fuData.list;
  const today=isoDatePlus(0);const due=fus.filter(function(x){return x.x.followUp<=today;}).length;
  const tabBtn=function(k,l){const on=tab===k;return <button key={k} onClick={function(){setTab(k);}} style={{padding:"9px 18px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?C.navy:C.border),background:on?C.navy:C.card,color:on?"#fff":C.textMuted}}>{l}</button>;};
  const setFu=function(fx,p){const r=latestRow(fx.r.id)||fx.r;const res=(r.results||[]).map(function(x,i){return i===fx.i?Object.assign({},x,p):x;});patch(r.id,{results:res});};
  const callNow=function(fx){
    const id=fx.r.id;const now=new Date().toISOString();
    flushDrafts(batch,true);
    setNoteFor(function(x){const o=Object.assign({},x);delete o[id];return o;});
    setFuFor(function(x){const o=Object.assign({},x);delete o[id];return o;});
    setDoneNote(function(x){const o=Object.assign({},x);delete o[id];return o;});
    delete noteLine.current[id];
    setShownAt(function(s){return Object.assign({},s,{[id]:now});});
    pinned.current=id;
    setBatch([id]);setTab("dialer");
  };

  return(<div>
    <Hdr t="Outbound" s="Pick the kind of lead, dial, and log what happened. Most recently mailed come up first. Converted files are kept out; do-not-call numbers, litigators and opted-out leads never show up here."/>
    {err&&<div style={{padding:"10px 14px",background:C.redBg,color:C.red,borderRadius:8,fontSize:12.5,marginBottom:12,fontWeight:600}}>{err}</div>}
    {swapNote&&<div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",background:C.orangeBg,color:C.orange,borderRadius:8,fontSize:12.5,marginBottom:12,fontWeight:700}}><span style={{flex:1}}>{swapNote}</span><button onClick={function(){setSwapNote("");}} style={{border:"none",background:"transparent",color:C.orange,cursor:"pointer",fontWeight:800}}>✕</button></div>}

    {/* ── Call stats: this session / today / all time ── */}
    {(function(){
      const lines=[
        ["Dials",function(o){return o.dials;}],
        ["People called",function(o){return o.people.size;}],
        ["Reached (spoke)",function(o){return o.spoke;}],
        ["Left voicemail",function(o){return o.voicemail;}],
        ["No answer",function(o){return o.no_answer;}],
        ["Dead / wrong #",function(o){return o.bad;}],
        ["Follow-ups set",function(o){return o.fu;}],
        ["Connect rate",function(o){return o.dials?Math.round(o.spoke*100/o.dials)+"%":"—";}],
      ];
      return(<div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"12px 16px",marginBottom:14,overflowX:"auto"}}>
        <table style={{borderCollapse:"collapse",width:"100%",minWidth:520,fontSize:12.5}}>
          <thead><tr>
            <th style={{textAlign:"left",padding:"4px 8px",fontSize:10.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>Call stats</th>
            {stats.map(function(c){return <th key={c.l} style={{textAlign:"right",padding:"4px 8px",fontSize:10.5,fontWeight:800,color:c.l==="This session"?C.accentLight:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>{c.l}</th>;})}
          </tr></thead>
          <tbody>{lines.map(function(rw){return(<tr key={rw[0]} style={{borderTop:"1px solid "+C.border}}>
            <td style={{padding:"5px 8px",color:C.textMuted,fontWeight:600}}>{rw[0]}</td>
            {stats.map(function(c){return <td key={c.l} style={{padding:"5px 8px",textAlign:"right",fontWeight:800,color:rw[0]==="Reached (spoke)"?C.green:C.text}}>{rw[1](c.o)}</td>;})}
          </tr>);})}</tbody>
        </table>
      </div>);
    })()}

    {/* ── Dialer / Follow-ups / Converted ── */}
    <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
      {tabBtn("dialer","Dialer")}
      {tabBtn("followups","Follow-ups ("+fus.length+")")}
      {tabBtn("converted","Converted ("+held.conv.length.toLocaleString()+")")}
      {due>0&&<span style={{fontSize:12,fontWeight:800,color:C.red,background:C.redBg,borderRadius:20,padding:"4px 10px"}}>{due} due</span>}
    </div>

    {tab==="followups"&&<div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
      {fus.length===0&&<div style={empty}>No follow-ups yet. When someone picks up, tick “Set a follow-up” on the note.</div>}
      {fuData.hidden>0&&<div style={{fontSize:11.5,color:C.textDim}}>{fuData.hidden} follow-up{fuData.hidden===1?"":"s"} not shown — the lead converted or was worked from the CRM. Work {fuData.hidden===1?"it":"them"} from the file.</div>}
      {fus.map(function(fx){
        const r=fx.r,over=fx.x.followUp<today,isToday=fx.x.followUp===today;const m=mailOf(r);const nums=dialable(r,ctx);
        return(<div key={r.id} style={{background:C.card,border:"1px solid "+(over?C.red+"66":isToday?C.orange+"66":C.border),borderLeft:"4px solid "+(over?C.red:isToday?C.orange:C.border),borderRadius:10,padding:"12px 16px"}}>
          <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:14.5,fontWeight:800,color:C.text}}>{r.name||"(no name)"}</span>
            <span style={{fontSize:12,fontWeight:800,color:over?C.red:isToday?C.orange:C.textMuted}}>{over?"Overdue · ":isToday?"Today · ":""}Follow up {fmtDay(fx.x.followUp)}</span>
            <span style={{marginLeft:"auto",fontSize:11,color:C.textDim}}>Spoke {new Date(fx.x.at).toLocaleDateString()}</span>
          </div>
          <div style={{fontSize:12,color:C.textMuted,marginTop:3}}>📍 {addr(r)}{m?" · ✉ Mailed "+fmtMail(m):""}</div>
          {fx.x.note&&<div style={{marginTop:6,fontSize:12.5,color:C.text}}>“{fx.x.note}”</div>}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginTop:10}}>
            {nums.slice(0,2).map(function(p){return <CallBtn key={p.n} phone={p.n}/>;})}
            {nums.length>0?<Btn small primary onClick={function(){callNow(fx);}}>Call now →</Btn>:<span style={{fontSize:11.5,color:C.textDim}}>No dialable numbers left</span>}
            <Btn small onClick={function(){setFu(fx,{followUpDone:true,followUpDoneAt:new Date().toISOString()});}} style={{color:C.green}}>✓ Done</Btn>
            <span style={{fontSize:11,color:C.textDim}}>Move to:</span>
            {[{d:1,l:"Tomorrow"},{d:7,l:"+1 week"},{d:30,l:"+30 days"}].map(function(p){return <button key={p.d} onClick={function(){setFu(fx,{followUp:isoDatePlus(p.d)});}} style={{padding:"4px 10px",borderRadius:20,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+C.border,background:"transparent",color:C.textMuted}}>{p.l}</button>;})}
          </div>
        </div>);
      })}
    </div>}

    {tab==="converted"&&<div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
      <div style={{fontSize:12,color:C.textMuted,lineHeight:1.5}}>These leads already became a pipeline file or were worked from the CRM, so none of their contact rows come up in the dialer or in follow-ups. Work them from the file.</div>
      {held.conv.length===0&&<div style={empty}>No converted or worked leads on the outbound list.</div>}
      {held.conv.slice(0,LIST_MAX).map(function(o){
        const r=o.rows[0],li=o.li,loan=li.loan;
        return(<div key={o.key} style={listCard}>
          <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:14.5,fontWeight:800,color:C.text}}>{r.name||(loan&&loan.b)||"(no name)"}</span>
            {holdTags(o.h)}
            {li.status&&(loan||li.convId)&&<span style={{fontSize:11,color:C.textDim}}>Lead: {li.status}</span>}
            {o.rows.length>1&&<span style={{fontSize:11,color:C.textDim}}>{o.rows.length} contact rows</span>}
          </div>
          <div style={{fontSize:12,color:C.textMuted,marginTop:3}}>📍 {addr(r)}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginTop:8}}>
            {loan?<Btn small primary disabled={!canOpenFile} onClick={function(){openFile(loan);}}>Open file {loan.id} →</Btn>
              :li.convId?<span style={{fontSize:11.5,color:C.textDim}}>File {li.convId} isn't loaded on this screen.</span>:null}
            {!loan&&r.lead_id&&onStartCall&&<Btn small onClick={function(){openLead(r);}}>Open lead in New Call →</Btn>}
          </div>
        </div>);
      })}
      {held.conv.length>LIST_MAX&&<div style={{fontSize:11.5,color:C.textDim}}>Showing the first {LIST_MAX} of {held.conv.length.toLocaleString()}.</div>}
    </div>}

    {tab==="dialer"&&<>
    {/* ── Filters: style + year + state ── */}
    <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"14px 16px",marginBottom:16}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
        {STYLES.map(function(s){const on=f.style===s.k;return <button key={s.k} onClick={function(){setF(Object.assign({},f,{style:s.k,year:""}));setSkipped([]);}} style={chip(on)}>{s.l} <span style={{opacity:.75,fontWeight:700}}>{(styleCounts[s.k]||0).toLocaleString()}</span></button>;})}
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
        <Sel value={f.year} onChange={function(e){setF(Object.assign({},f,{year:e.target.value}));setSkipped([]);}} options={[{value:"",label:"Any loan year"}].concat(years)}/>
        <Sel value={f.state} onChange={function(e){setF(Object.assign({},f,{state:e.target.value,year:""}));setSkipped([]);}} options={[{value:"",label:"All states"}].concat(states.map(function(s){return{value:s,label:s};}))}/>
        <span style={{fontSize:12.5,color:C.textMuted}}><strong style={{color:C.text}}>{queue.length.toLocaleString()}</strong> ready to dial</span>
        {f.style==="ll"&&<span style={{fontSize:11.5,color:C.textDim}}>HECMs whose value was over the FHA limit the year they closed (today's limit: ${Number(hecmLimit||0).toLocaleString()})</span>}
      </div>
      {held.conv.length>0&&<div style={{fontSize:11.5,color:C.textDim,marginTop:10}}>
        Kept out: <button onClick={function(){setTab("converted");}} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontSize:11.5,fontWeight:700,color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>{held.conv.length.toLocaleString()} converted / worked</button>
      </div>}
    </div>

    {/* ── Three cards ── */}
    {!flagsOk?<div style={empty}>{flagErr?"Couldn't check who has opted out ("+flagErr+"). Nobody is offered until the lead list finishes loading.":"Checking who has opted out…"}</div>:
    cards.length===0?<div style={empty}>Nobody left to dial for this filter. Try another year or style.</div>:
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14,alignItems:"start"}}>
      {cards.map(function(r){
        const h=info.get(r.id);
        const since=sinceOf(r.id);
        const nums=h&&h.held?[]:cardNums(r,ctx,since);
        const sres=sinceResults(r);
        const resultFor=function(n){const s=sres.filter(function(x){return x.n===n;});return s.length?s[s.length-1]:null;};
        const resolved=isResolved(r);
        const noteOpen=Object.prototype.hasOwnProperty.call(noteFor,r.id);
        const draft=String(noteFor[r.id]||"");
        const nl=noteLine.current[r.id];
        const savedHint=nl&&draft.trim()===nl.text;
        return(<div key={r.id} style={{background:C.card,border:"2px solid "+(resolved?C.green+"88":C.border),borderRadius:14,padding:"16px 18px",opacity:resolved?.75:1,transition:"opacity .2s"}}>
          <div style={{fontSize:16,fontWeight:800,color:C.text,lineHeight:1.25}}>{r.name||"(no name)"}</div>
          <div style={{fontSize:12,color:C.textMuted,marginTop:3}}>📍 {addr(r)} {r.zip}</div>
          {(function(){const m=mailOf(r);return(
            <div style={{fontSize:12,fontWeight:700,marginTop:6,color:m?C.clay:C.textDim}}>
              {m?"✉ Mailed "+fmtMail(m)+(m.campaign?" · "+m.campaign:""):(leadsReady===false?"Loading mail history…":"No mailing on record")}
            </div>);})()}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
            {badges(r).map(function(t,i){return <span key={i} style={{fontSize:10.5,fontWeight:700,color:C.text,background:C.surfaceAlt,border:"1px solid "+C.border,borderRadius:6,padding:"2px 7px"}}>{t}</span>;})}
          </div>
          {r.call_count>0&&!sres.length&&<div style={{fontSize:11,color:C.textDim,marginTop:6}}>Tried {r.call_count}× before · last: {RESULT_LABEL[r.last_result]||r.last_result||"—"}</div>}

          {/* held mid-session (converted, opted out…) → no more numbers */}
          {h&&h.held&&<div style={{marginTop:10,padding:"8px 10px",borderRadius:8,background:C.surfaceAlt,border:"1px solid "+C.border}}>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>{holdTags(h)}</div>
            {h.conv&&h.conv.loan&&!h.hide&&<div style={{marginTop:8}}><Btn small primary disabled={!canOpenFile} onClick={function(){openFile(h.conv.loan);}}>Open file {h.conv.loan.id} →</Btn></div>}
          </div>}

          {/* running log — every attempt on this contact, newest first */}
          {(r.results||[]).length>0&&<div style={{marginTop:10}}>
            <button onClick={function(){setLogOpen(function(x){return Object.assign({},x,{[r.id]:!x[r.id]});});}} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontSize:11.5,fontWeight:800,color:C.accentLight,fontFamily:"'Mulish',system-ui,sans-serif"}}>
              Activity log ({(r.results||[]).length}) {logOpen[r.id]?"▾":"▸"}
            </button>
            {logOpen[r.id]&&<div style={{marginTop:6,maxHeight:160,overflowY:"auto",borderLeft:"2px solid "+C.border,paddingLeft:10,display:"flex",flexDirection:"column",gap:5}}>
              {(r.results||[]).slice().reverse().map(function(x,i){const d=new Date(x.at);const col=(RESULTS.find(function(y){return y.k===x.result;})||{}).c;return(
                <div key={i} style={{fontSize:11.5,color:C.text,lineHeight:1.4}}>
                  <span style={{color:C.textDim}}>{d.toLocaleDateString()} {d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>{" · "}{fmtPhone(x.n)}{" · "}<strong style={{color:col}}>{RESULT_LABEL[x.result]||x.result}</strong>
                  {x.note?<div style={{color:C.textMuted,marginTop:1}}>“{x.note}”</div>:null}
                  {x.followUp?<div style={{color:x.followUpDone?C.textDim:C.orange,fontWeight:700,marginTop:1}}>{x.followUpDone?"✓ Follow-up "+fmtDay(x.followUp)+" closed":"⏰ Follow up "+fmtDay(x.followUp)}</div>:null}
                </div>);})}
            </div>}
          </div>}

          {/* numbers + results */}
          <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:12}}>
            {nums.map(function(p){
              const res=resultFor(p.n);
              return(<div key={p.n} style={{padding:"10px 12px",borderRadius:10,background:C.surfaceAlt,border:"1px solid "+C.border}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <CallBtn phone={p.n}/>
                  <span style={{fontSize:11,color:C.textDim}}>{[p.type,p.best?"best":""].filter(Boolean).join(" · ")}</span>
                  {res&&<button onClick={function(){undo(r,p.n);}} style={{marginLeft:"auto",background:"none",border:"none",color:C.textDim,fontSize:11,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>undo</button>}
                </div>
                {res?<div style={{marginTop:8,fontSize:12,fontWeight:800,color:(RESULTS.find(function(x){return x.k===res.result;})||{}).c}}>✓ {RESULT_LABEL[res.result]}</div>
                :<div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8}}>
                  {RESULTS.map(function(x){return <button key={x.k} onClick={function(){record(r,p.n,x.k);}} style={{padding:"5px 9px",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+x.c+"66",background:x.k==="spoke"?x.c+"18":"transparent",color:x.c}}>{x.l}</button>;})}
                </div>}
              </div>);
            })}
          </div>

          {/* spoke → notes (save themselves on blur, Next 3 and leaving) */}
          {noteOpen&&!doneNote[r.id]&&<div style={{marginTop:12}}>
            <textarea autoFocus value={draft} onChange={function(e){const v=e.target.value;setNoteFor(function(x){return Object.assign({},x,{[r.id]:v});});}}
              onBlur={function(e){commitNote(r.id,e.target.value,"");}}
              placeholder="What did they say? Saved on this contact's activity log."
              style={{width:"100%",minHeight:72,padding:"10px 12px",borderRadius:8,border:"1px solid "+C.green+"66",background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>
            {draft.trim()&&<div style={{fontSize:11,fontWeight:700,marginTop:3,color:savedHint?C.green:C.textDim}}>{savedHint?"✓ Saved":"Saves when you click away"}</div>}
            {(function(){const fu=fuFor[r.id]||{on:false,date:""};const set=function(p){setFuFor(function(x){return Object.assign({},x,{[r.id]:Object.assign({},fu,p)});});};return(
              <div style={{marginTop:8,padding:"8px 10px",borderRadius:8,border:"1px solid "+(fu.on?C.orange+"66":C.border),background:fu.on?C.orangeBg:"transparent"}}>
                <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,fontWeight:700,color:fu.on?C.orange:C.textMuted,cursor:"pointer"}}>
                  <input type="checkbox" checked={fu.on} onChange={function(e){set({on:e.target.checked,date:fu.date||isoDatePlus(7)});}} style={{width:16,height:16,cursor:"pointer"}}/>
                  Set a follow-up
                </label>
                {fu.on&&<div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginTop:8}}>
                  {FU_PICKS.map(function(p){const on=fu.date===isoDatePlus(p.d);return <button key={p.d} onClick={function(){set({date:isoDatePlus(p.d)});}} style={{padding:"4px 10px",borderRadius:20,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?C.orange:C.border),background:on?C.orange:"transparent",color:on?"#fff":C.textMuted}}>{p.l}</button>;})}
                  <input type="date" value={fu.date} onChange={function(e){set({date:e.target.value});}} style={{padding:"4px 8px",borderRadius:6,border:"1px solid "+C.border,fontSize:12,colorScheme:"light",fontFamily:"'Mulish',system-ui,sans-serif"}}/>
                </div>}
              </div>);})()}
            <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
              <Btn small primary onClick={function(){saveNote(r,false);}} disabled={!draft.trim()&&!(fuFor[r.id]&&fuFor[r.id].on&&fuFor[r.id].date)}>Save</Btn>
              {!draft.trim()&&<Btn small onClick={function(){saveNote(r,true);}}>No note</Btn>}
              {r.lead_id&&<Btn small onClick={function(){openLead(r);}}>Open in New Call →</Btn>}
            </div>
          </div>}
          {doneNote[r.id]&&r.lead_id&&!(h&&h.held)&&<div style={{marginTop:10}}><Btn small onClick={function(){openLead(r);}}>Open in New Call →</Btn></div>}
          {resolved&&!(h&&h.held)&&<div style={{marginTop:10,fontSize:11.5,fontWeight:800,color:C.green}}>Done</div>}
        </div>);
      })}
    </div>}

    </>}

    {/* ── Running activity log: every dial across the list, newest first ── */}
    {acts.length>0&&<div style={{marginTop:22,background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"14px 16px"}}>
      <div style={{fontSize:13,fontWeight:800,color:C.text,marginBottom:8}}>Activity log <span style={{fontWeight:600,color:C.textDim}}>· {acts.length.toLocaleString()} dials</span></div>
      <div style={{maxHeight:260,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
        {acts.slice(0,300).map(function(a,i){const d=new Date(a.x.at);const col=(RESULTS.find(function(y){return y.k===a.x.result;})||{}).c;return(
          <div key={i} style={{fontSize:12,color:C.text,lineHeight:1.45,borderBottom:"1px solid "+C.border,paddingBottom:5}}>
            <span style={{color:C.textDim}}>{d.toLocaleDateString()} {d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>{" · "}<strong>{a.r.name||"(no name)"}</strong>{" · "}{fmtPhone(a.x.n)}{" · "}<strong style={{color:col}}>{RESULT_LABEL[a.x.result]||a.x.result}</strong>
            {a.x.note?<span style={{color:C.textMuted}}> — “{a.x.note}”</span>:null}
          </div>);})}
      </div>
    </div>}

    {tab==="dialer"&&flagsOk&&cards.length>0&&<div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:12,marginTop:16}}>
      <span style={{fontSize:12,color:C.textMuted}}>{cards.filter(isResolved).length} of {cards.length} done{allDone?" — loading the next three…":""}</span>
      <Btn primary onClick={next}>Next {BATCH} →</Btn>
    </div>}
  </div>);
}

export { Outbound, dialable, cardNums, buildDialCtx, isLitigatorContact, leadHold, openFollowUp, mailDay, mailWhen, styleBuckets };
